import type { CalendarMeeting, CalendarProvider } from '@ai-meeting/shared'
import { ListIcon } from '@/components/Icons'
import { MEDIA_LG_UP, useMediaQuery } from '@/hooks/useMediaQuery'
import { useUiStrings } from '@/hooks/useUiStrings'
import {
  CalendarMeetingGlyph,
  CalendarMeetingTime,
} from '@/pages/Home/components/CalendarMeetingRow'
import { isBriefEligible } from './briefVisibility'

/** Matches the app's desktop breakpoint, where the sidebar leaves the table room. */
const WIDE_LIST = MEDIA_LG_UP

interface MeetingTableProps {
  items: CalendarMeeting[]
  provider: CalendarProvider
  onOpenBrief?: (meeting: CalendarMeeting) => void
  /**
   * Event ids that may offer Brief. Computed from the full provider week —
   * never from the page currently on screen.
   */
  briefEligibleIds?: ReadonlySet<string>
  emptyLabel: string
  /** Accessible table name, e.g. "Teams meetings". */
  tableLabel: string
}

/**
 * The full calendar listing.
 *
 * Brief is offered on rows whose id is in `briefEligibleIds` (nearest active
 * meeting in the week per project, plus unmapped meetings in the window).
 * The other rows keep Join so the reader can still enter without a brief.
 *
 * Below the desktop breakpoint the four-column table becomes a list of cards.
 * A 640px table on a 390px screen leaves Join and Brief off-screen, and an
 * action you have to discover by dragging sideways is an action most people
 * never take.
 */
export default function MeetingTable({
  items,
  provider,
  onOpenBrief,
  briefEligibleIds,
  emptyLabel,
  tableLabel,
}: MeetingTableProps) {
  const { t } = useUiStrings()
  const isWide = useMediaQuery(WIDE_LIST)
  const joinLabel = provider === 'google' ? t.googleCalendarJoin : t.teamsCalendarJoin
  const briefEnabled = Boolean(onOpenBrief)
  const eligible = briefEligibleIds ?? new Set<string>()

  const actions = (item: CalendarMeeting) => {
    const briefAvailable = briefEnabled && isBriefEligible(item, eligible)

    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {briefAvailable && (
          <button
            type="button"
            onClick={() => onOpenBrief?.(item)}
            title={`${t.briefView}: ${item.title}`}
            aria-label={`${t.briefButton}: ${item.title}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] px-2.5 py-1 text-[11px] font-medium text-[var(--accent)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <ListIcon className="h-3 w-3" />
            <span>{t.briefButton}</span>
          </button>
        )}

        <a
          href={item.joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            event.preventDefault()
            window.open(item.joinUrl, '_blank', 'noopener,noreferrer')
          }}
          aria-label={`${joinLabel}: ${item.title}`}
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-[#464eb8] px-3 py-1 text-[11px] font-medium text-white shadow-sm transition-colors hover:bg-[#3b429f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#464eb8]/40"
        >
          {joinLabel}
        </a>
      </div>
    )
  }

  if (!isWide) {
    return (
      <div aria-label={tableLabel} role="group">
        {items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-[var(--text-secondary)]">
            {emptyLabel}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--border-color)]">
            {items.map((item) => (
              <li key={item.id} className="flex flex-col gap-2.5 px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)] dark:bg-blue-500/10">
                    <CalendarMeetingGlyph provider={provider} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="break-words font-semibold text-[var(--text-primary)]">
                        {item.title}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
                      <CalendarMeetingTime meeting={item} />
                    </p>
                    <p
                      className="mt-0.5 truncate text-xs text-[var(--text-tertiary)]"
                      title={item.organizer || undefined}
                    >
                      {item.organizer || '—'}
                    </p>
                  </div>
                </div>
                {actions(item)}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-sm" aria-label={tableLabel}>
        <thead className="border-b border-[var(--border-color)] bg-[var(--bg-primary)]">
          <tr className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 sm:px-5">
              {t.meetingNameCol}
            </th>
            <th scope="col" className="px-4 py-3 sm:px-5">
              {t.teamsCalendarScheduledTime}
            </th>
            <th scope="col" className="px-4 py-3 sm:px-5">
              {t.teamsCalendarOrganizer}
            </th>
            <th scope="col" className="px-4 py-3 text-right sm:px-5">
              {t.actionCol}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td
                colSpan={4}
                className="px-4 py-10 text-center text-sm text-[var(--text-secondary)]"
              >
                {emptyLabel}
              </td>
            </tr>
          )}

          {items.map((item) => (
            <tr
              key={item.id}
              className="border-b border-[var(--border-color)] last:border-b-0 hover:bg-[var(--bg-primary)]"
            >
              <td className="max-w-80 px-4 py-4 sm:px-5">
                <div className="flex min-w-[220px] items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)] dark:bg-blue-500/10">
                    <CalendarMeetingGlyph provider={provider} />
                  </div>
                  <span className="min-w-0 break-words font-semibold text-[var(--text-primary)]">
                    {item.title}
                  </span>
                </div>
              </td>

              <td className="whitespace-nowrap px-4 py-4 text-[var(--text-secondary)] sm:px-5">
                <CalendarMeetingTime meeting={item} />
              </td>

              <td className="max-w-60 break-words px-4 py-4 text-[var(--text-secondary)] sm:px-5">
                {item.organizer || '—'}
              </td>

              <td className="px-4 py-4 sm:px-5">{actions(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
